// Read for clarity

import { ProjectDocument } from "@/types/project";

// Purpose:
// Contains general-purpose helper functions that don’t fit elsewhere. These functions can simplify repetitive tasks, like formatting dates or strings.

// Example Use:
// Formatting a date for display in the UI.

// Content:

// export const formatDate = (date: string): string => {
//   return new Date(date).toLocaleDateString('en-US', {
//     year: 'numeric',
//     month: 'short',
//     day: 'numeric',
//   });
// };
// How It’s Used:

// import { formatDate } from '../utils/helpers';

// const project = { title: 'New Project', createdAt: '2024-11-27T10:00:00Z' };
// console.log(`Project created on: ${formatDate(project.createdAt)}`);
// Output:

export const getDocumentIcon = (type: string): string => {
  const icons: Record<string, string> = {
    docx: "/images/documentLogos/docx-icon.png", // Replace with actual path to icons
    doc: "/images/documentLogos/docx-icon.png",
    xlsx: "/images/documentLogos/xlsx-icon.png",
    pptx: "/images/documentLogos/ppt-icon.png",
    psd: "/images/documentLogos/psd-icon.png",
    ai: "/images/documentLogos/ai-icon.png",
    jpg: "/images/documentLogos/jpg-icon.png",
    png: "/images/documentLogos/png-icon.png",
    pdf: "/images/documentLogos/pdf-icon.png",
  };
  return icons[type] || "/images/documentLogos/pdf-icon.png";
};

export const getFileType = (
  fileName: string
): ProjectDocument["document_type"] => {
  const sanitizedFileName = fileName.trim();
  const extension = sanitizedFileName.split(".").pop()?.toLowerCase();

  switch (extension) {
    case "docx":
    case "doc":
      return "docx";
    case "xlsx":
      return "xlsx";
    case "pptx":
      return "pptx";
    case "pdf":
      return "pdf";
    case "psd":
      return "psd";
    case "ai":
      return "ai";
    case "jpg":
    case "jpeg": // Optional: treat .jpg and .jpeg the same
      return "jpg";
    case "png": // Add handling for .png
      return "png"; // Assuming both .jpg and .png are treated as "jpg" in your type system
    default:
      return "pdf"; // Default fallback type
  }
};

export const formatDate = (dateString: string) => {
  const date = new Date(dateString);

  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Months are 0-based
  const year = String(date.getUTCFullYear()).slice(-2);
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");

  return day != "NaN" ? `${day}/${month}/${year} - ${hours}:${minutes}` : "";
};
